import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { shipmentAPI, driverPublicAPI, driversAPI } from "../../services/api";
import { AddCommentPayload, Comment, CommentState } from "../../types";
import { enqueueAction } from "../../utils/offlineQueue";

type CommentSource = "driver" | "shipment" | "other";

/**
 * Returns the correct API instance based on the source and protection level.
 * - If source = "driver" → uses getDriverAPI()
 * - Otherwise → uses shipmentAPI
 */
export const getApi = (source: CommentSource, isProtected: boolean = false) => {
  if (source === "driver") {
    return isProtected ? driversAPI : driverPublicAPI;
  }
  return shipmentAPI;
};

/**
 * Add a comment exactly once, whichever path delivers it first.
 *
 * Every comment now arrives twice for its author: once as the HTTP response to
 * their own POST, and once over the socket (they are in the shipment room like
 * any other viewer). The socket usually wins — the server emits immediately
 * after save(), while the response still has to travel — so neither path can
 * assume it is first. Keyed on _id, so both are idempotent.
 *
 * Appended, not prepended: the list is oldest-first.
 */
const upsertComment = (state: CommentState, incoming: Comment) => {
  if (!incoming?._id) return;

  const existing = state.comments.findIndex((c) => c._id === incoming._id);
  if (existing !== -1) {
    // Keep the richer copy — an optimistic/offline row may lack server fields.
    state.comments[existing] = { ...state.comments[existing], ...incoming };
    return;
  }

  state.comments.push(incoming);
  state.pagination.total += 1;
};

/** Mirror of upsertComment: only adjust the total if this call did the removing. */
const removeComment = (state: CommentState, commentId: string) => {
  const before = state.comments.length;
  state.comments = state.comments.filter((c) => c._id !== commentId);
  if (state.comments.length < before) state.pagination.total -= 1;
};

// Initial state
const initialState: CommentState = {
  comments: [],
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  },
};

// Fetch comments
export const getComments = createAsyncThunk(
  "comment/getComments",
  async (
    {
      source,
      token,
      params,
      isProtected = false,
    }: {
      source: CommentSource;
      token: string;
      params?: any;
      isProtected?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const api = getApi(source, isProtected);
      const response = await api.getShipmentComments(token, params);
      const data = response.data.data;

      // Cache driver comments based on protection level
      if (source === "driver") {
        if (!isProtected) {
          // Public driver - use existing function
          const { cacheDriverComments } = await import("../../utils/indexedDB");
          cacheDriverComments(token, data.comments).catch((err) => {
            console.warn(
              "[Offline] Failed to cache public driver comments:",
              err,
            );
          });
        } else {
          // Protected driver - use new function
          const { cacheProtectedDriverComments } =
            await import("../../utils/indexedDB");
          cacheProtectedDriverComments(token, data.comments).catch((err) => {
            console.warn(
              "[Offline] Failed to cache protected driver comments:",
              err,
            );
          });
        }
      }

      return data;
    } catch (error: any) {
      // Offline fallback for driver comments (both public and protected)
      if (
        source === "driver" &&
        (!navigator.onLine || error.code === "ERR_NETWORK")
      ) {
        try {
          let cached;

          if (!isProtected) {
            // Public driver
            const { getCachedDriverComments } =
              await import("../../utils/indexedDB");
            cached = await getCachedDriverComments(token);
          } else {
            // Protected driver
            const { getCachedProtectedDriverComments } =
              await import("../../utils/indexedDB");
            cached = await getCachedProtectedDriverComments(token);
          }

          if (cached && cached.length > 0) {
            // console.log(
            //   `[Offline] Serving ${isProtected ? "protected" : "public"} driver comments from cache:`,
            //   cached.length,
            // );
            return {
              comments: cached,
              pagination: {
                page: 1,
                limit: cached.length,
                total: cached.length,
                pages: 1,
              },
            };
          }
        } catch (cacheErr) {
          console.error(
            "[Offline] Driver comments cache retrieval failed:",
            cacheErr,
          );
        }
      }

      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch comments",
      );
    }
  },
);

// Add comment
export const addComment = createAsyncThunk(
  "comment/addComment",
  async (
    {
      source,
      shipmentId,
      commentData,
      isProtected = false,
    }: {
      source: CommentSource;
      shipmentId: string;
      commentData: AddCommentPayload;
      isProtected?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      if (!navigator.onLine) {
        await enqueueAction(
          "ADD_COMMENT",
          { token: shipmentId, commentData, isProtected },
          shipmentId,
        );

        const now = new Date().toISOString();
        return {
          _id: `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          shipmentId,
          companyId: "",
          message: commentData.message,
          addedBy: source === "driver" ? "driver" : "user",
          shipmentStatus: "Order",
          createdAt: now,
          updatedAt: now,
        };
      }
      const api = getApi(source, isProtected);
      const response = await api.addShipmentComment(shipmentId, commentData);
      return response.data.data.comment;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add comment",
      );
    }
  },
);

// Delete comment
export const deleteComment = createAsyncThunk(
  "comment/deleteComment",
  async (
    {
      source,
      shipmentId,
      commentId,
      isProtected = false,
    }: {
      source: CommentSource;
      shipmentId: string;
      commentId: string;
      isProtected?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      if (!navigator.onLine) {
        await enqueueAction(
          "DELETE_COMMENT",
          { token: shipmentId, commentId, isProtected },
          shipmentId,
        );
        return commentId;
      }
      const api = getApi(source, isProtected);
      await api.deleteShipmentComment(shipmentId, commentId);
      return commentId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete comment",
      );
    }
  },
);

const commentSlice = createSlice({
  name: "comment",
  initialState,
  reducers: {
    clearCommentError: (state) => {
      state.error = null;
    },

    /** A comment pushed over the socket by anyone viewing this shipment. */
    commentReceived: (state, action: PayloadAction<Comment>) => {
      upsertComment(state, action.payload);
    },

    /** Someone else deleted a comment; drop it rather than leave a ghost row. */
    commentRemoved: (state, action: PayloadAction<string>) => {
      removeComment(state, action.payload);
    },

    resetCommentState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get comments
      .addCase(getComments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getComments.fulfilled, (state, action) => {
        state.loading = false;
        state.comments = action.payload.comments;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getComments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Add comment
      .addCase(addComment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addComment.fulfilled, (state, action) => {
        state.loading = false;
        // The author is in the shipment room too, so their own comment also
        // arrives over the socket — and the push routinely wins this race,
        // because the server emits right after save() while this response still
        // has to travel back. Whichever lands first, the comment is added once.
        upsertComment(state, action.payload);
        state.error = null;
      })
      .addCase(addComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete comment
      .addCase(deleteComment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.loading = false;
        // Same race as above, mirrored: `commentRemoved` may already have taken
        // it out. Decrement only if this call is the one that removed it, or
        // the total drifts below the real count.
        removeComment(state, action.payload);
        state.error = null;
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearCommentError,
  resetCommentState,
  commentReceived,
  commentRemoved,
} = commentSlice.actions;
export default commentSlice.reducer;
